import React, { useState, useMemo, useCallback, Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters } from '../../../redux/slices/filterSlice';
import {
	TextField,
	InputAdornment,
	Popper,
	Paper,
	List,
	ListSubheader,
	ListItem,
	Typography,
	Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { SearchResult, MatchedEntityField } from '../../../api/models/searchResult.model';
import { useLazyGeneralSearchQuery } from '../../../api/generalSearch.api';
import './GeneralSearch.scss';
import { selectAccount } from '../../../redux/slices/accountSlice';
import { isInvalidSearchTermError } from '../../../utils/helpers';

export default function GeneralSearchBar() {
	function useDebouncedValue<T>(value: T, delay: number): T {
		const [debounced, setDebounced] = useState(value);
		useEffect(() => {
			const handler = setTimeout(() => setDebounced(value), delay);
			return () => clearTimeout(handler);
		}, [value, delay]);
		return debounced;
	}

	const dispatch = useDispatch();
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
	const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [triggerSearch, {data, error}] = useLazyGeneralSearchQuery();
	const account = useSelector(selectAccount);
	const [localData, setLocalData] = useState<SearchResult[] | undefined>(undefined);

	useEffect(() => {
		if (debouncedSearchTerm) {
			setIsLoading(true);
			setLocalData(undefined); // Clear previous results
			triggerSearch({
				accountId: account?.accountId,
				accountTypeCode: account?.accountTypeCode,
				sourceId: account?.sourceId,
				searchTerm: debouncedSearchTerm,
			});
		}
	}, [debouncedSearchTerm]);

	useEffect(() => {
		if (isInvalidSearchTermError(error)) {
			setLocalData([]);
			setIsLoading(false);
			return;
		}
		setIsLoading(false);
		if (data) setLocalData(data as SearchResult[]);
	}, [data, error]);

	const groupedResults: Record<string, SearchResult[]> = useMemo(() => {
		if (!localData) return {};
		// API already groups by matchedEntity, but ensure structure
		return (localData as SearchResult[]).reduce(
			(acc: Record<string, SearchResult[]>, result: SearchResult) => {
				const key = result.matchedEntity;
				acc[key] = acc[key] ? [...acc[key], result] : [result];
				return acc;
			},
			{}
		);
	}, [localData]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setAnchorEl(e.target);
	};

	// When a value is clicked, set the filter in Redux
	const handleValueClick = useCallback(
		(field: MatchedEntityField, value: string) => {
			dispatch(setFilters({ [field.name]: value }));
			setSearchTerm('');
		},
		[dispatch]
	);

	const showInvalidSearchError = isInvalidSearchTermError(error);
	const open =
		Boolean(anchorEl) &&
		searchTerm.length > 0 &&
		(isLoading || data !== undefined || showInvalidSearchError);

	return (
		<Box sx={{ minWidth: 300, maxWidth: 400 }}>
			<TextField
				fullWidth
				className="general-search-bar"
				size="small"
				variant="outlined"
				placeholder="Search..."
				value={searchTerm}
				disabled={(!!account && !account.accountId)}
				onChange={handleInputChange}
				slotProps={{
					input: {
						endAdornment: (
							<InputAdornment position="end">
								{' '}
								<SearchIcon />
							</InputAdornment>
						),
					},
				}}
				onFocus={(e) => setAnchorEl(e.target as HTMLInputElement)}
				onBlur={() => {
				setTimeout(() => setAnchorEl(null), 500);
				}}
				autoComplete="off"
			/>
			<Popper
				open={open}
				role="dialog"
				anchorEl={anchorEl}
				placement="bottom-start"
				style={{ zIndex: 1300 }}
			>
				<Paper
					sx={{ width: anchorEl?.offsetWidth ?? 350, maxHeight: 350, overflowY: 'auto', mt: .5 }}
				>
					<List dense>
						{showInvalidSearchError ? (
							<ListItem>
								<Typography variant="body2" color="text.secondary">
									Your search phrase contains prohibited characters. Please revise your search.
								</Typography>
							</ListItem>
						) : isLoading ? (
							<ListItem>
								<Typography variant="body2" color="text.secondary">
									Loading results...
								</Typography>
							</ListItem>
						) : Object.keys(groupedResults).length === 0 ? (
							<ListItem>
								<Typography variant="body2" color="text.secondary">
									No results found.
								</Typography>
							</ListItem>
						) : (
							Object.entries(groupedResults).map(([entityType, results]) => (
								<Fragment key={entityType}>
									<ListSubheader
										sx={{ fontWeight: 600, color: (theme) => theme.palette.primary.main }}
									>
										{entityType}
									</ListSubheader>
									{results.map((result: SearchResult, idx: number) => (
										<ListItem key={idx}>
											<Box>
												{result.fields.map((field: MatchedEntityField) => (
													<Box key={field.name} sx={{ mb: 1 }}>
														<Typography variant="subtitle2">{field.label}:</Typography>
														{field.values.map((value, vIdx) => (
															<Typography
															variant="body2"
															key={vIdx}
															className="search-result-value"
															onClick={() => {
															handleValueClick(field, value);
															}}
															>
																{value}
															</Typography>
														))}
												</Box>
											))}
										</Box>
									</ListItem>
								))}
							</Fragment>
							))
						)}
					</List>
				</Paper>
			</Popper>
		</Box>
	);
}
